import { useEffect, useState } from "react";
import {
  FileWarning, CheckCircle2, AlertTriangle, Clock, X, Loader2,
  Receipt, Search, Filter, RefreshCcw, QrCode, BellRing,
} from "lucide-react";
import api, { fmtMXN } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import QrCfdiScanner from "@/components/QrCfdiScanner";
import { toast } from "sonner";

const SEVERITY_COLOR = {
  green: { bg: "rgba(34,197,94,0.1)", color: "#22c55e", label: "Reciente" },
  yellow: { bg: "rgba(251,191,36,0.15)", color: "#fbbf24", label: "Pendiente" },
  red: { bg: "rgba(239,68,68,0.15)", color: "#ef4444", label: "Urgente" },
};

export default function BillingPending() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filterSev, setFilterSev] = useState("");
  const [markModal, setMarkModal] = useState(null); // expense item
  const [noInvoiceModal, setNoInvoiceModal] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/billing/pending-invoices");
      setData(data);
    } catch (e) {
      toast.error("No se pudieron cargar las facturas pendientes");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const items = (data?.items || []).filter((it) => {
    if (filterSev && it.severity !== filterSev) return false;
    if (!search.trim()) return true;
    const q = search.trim().toLowerCase();
    return (
      (it.vendor || "").toLowerCase().includes(q) ||
      (it.project_code || "").toLowerCase().includes(q) ||
      (it.project_name || "").toLowerCase().includes(q) ||
      (it.description || "").toLowerCase().includes(q)
    );
  });

  const k = data?.kpis;

  return (
    <div data-testid="billing-pending-page">
      <PageHeader
        subtitle="Gastos pagados con efectivo o tarjeta que aún no tienen el CFDI del proveedor"
        title="Facturas Pendientes de Recibir"
        actions={
          <>
            <button
              onClick={async () => {
                try {
                  const { data: r } = await api.post("/billing/reminders/run");
                  if (r.sent) toast.success(`Recordatorio enviado a ${r.notified} usuarios (${r.count} gastos pendientes)`);
                  else toast.info("No hay facturas pendientes que recordar");
                } catch {
                  toast.error("No se pudo enviar el recordatorio");
                }
              }}
              data-testid="send-reminder-btn"
              className="btn-ghost px-3 py-2 rounded-sm flex items-center gap-2 text-xs uppercase tracking-widest"
              style={{ borderColor: "rgba(34,211,238,0.4)", color: "var(--cyan)" }}
              title="Además del aviso diario automático (8:00 AM), puedes enviarlo ahora"
            >
              <BellRing className="w-4 h-4" /> Enviar recordatorio
            </button>
            <button
              onClick={load}
              data-testid="refresh-btn"
              className="btn-ghost px-3 py-2 rounded-sm flex items-center gap-2 text-xs uppercase tracking-widest"
            >
              <RefreshCcw className="w-4 h-4" /> Actualizar
            </button>
          </>
        }
      />

      <div className="px-6 lg:px-8 py-6 space-y-5">
        {/* KPIs */}
        {k && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="kpis">
            <Kpi label="Σ Monto pendiente" value={fmtMXN(k.total_amount)} icon={Receipt} color="var(--cyan)" />
            <Kpi label="# Gastos sin CFDI" value={k.count} icon={FileWarning} color="var(--magenta)" />
            <Kpi label="Días hábiles prom." value={`${k.avg_business_days || 0}d`} icon={Clock} color="#fbbf24" />
            <Kpi label="🔴 Urgentes (5+ días)" value={k.by_severity?.red || 0} icon={AlertTriangle} color="#ef4444" />
          </div>
        )}

        {/* Filtros */}
        <div className="panel p-3 flex items-center gap-2 flex-wrap">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
            <input
              data-testid="search-input"
              placeholder="Buscar proveedor, proyecto, concepto…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="input-cyber pl-10 pr-3 py-2 text-sm w-full"
            />
          </div>
          <div className="flex items-center gap-1">
            <Filter className="w-3.5 h-3.5 text-slate-500" />
            {["", "red", "yellow", "green"].map((s) => (
              <button
                key={s || "all"}
                data-testid={`filter-${s || "all"}`}
                onClick={() => setFilterSev(s)}
                className="px-2.5 py-1.5 text-[10px] uppercase tracking-widest rounded-sm"
                style={{
                  background: filterSev === s ? "rgba(34,211,238,0.15)" : "rgba(15,23,42,0.6)",
                  color: filterSev === s ? "var(--cyan)" : "#94a3b8",
                  border: filterSev === s ? "1px solid var(--cyan)" : "1px solid var(--border)",
                  fontWeight: filterSev === s ? 700 : 500,
                }}
              >
                {s === "" ? "Todos" : SEVERITY_COLOR[s].label}
              </button>
            ))}
          </div>
        </div>

        {/* Tabla */}
        {loading ? (
          <div className="panel p-10 text-center text-slate-400">Cargando…</div>
        ) : items.length === 0 ? (
          <div className="panel p-12 text-center text-slate-400">
            <CheckCircle2 className="w-12 h-12 mx-auto mb-3 text-green-400" />
            <div className="text-lg font-display font-bold text-slate-200">¡Sin pendientes! 🎉</div>
            <div className="text-xs text-slate-500 mt-1">No hay gastos esperando CFDI del proveedor.</div>
          </div>
        ) : (
          <div className="panel overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="pending-table">
                <thead style={{ background: "rgba(15,23,42,0.8)" }}>
                  <tr className="text-left text-[10px] uppercase tracking-widest text-slate-500">
                    <th className="px-3 py-2.5">Días</th>
                    <th className="px-3 py-2.5">Fecha</th>
                    <th className="px-3 py-2.5">Proveedor</th>
                    <th className="px-3 py-2.5">Proyecto</th>
                    <th className="px-3 py-2.5">Concepto</th>
                    <th className="px-3 py-2.5 text-right">Monto</th>
                    <th className="px-3 py-2.5 text-center">Acciones</th>
                  </tr>
                </thead>
                <tbody className="divide-y" style={{ borderColor: "var(--border)" }}>
                  {items.map((it) => {
                    const sev = SEVERITY_COLOR[it.severity];
                    return (
                      <tr key={it.id} data-testid={`row-${it.id}`} className="hover:bg-cyan-500/5">
                        <td className="px-3 py-2.5">
                          <span
                            className="inline-flex items-center px-2 py-0.5 rounded-sm text-[11px] font-mono font-bold"
                            style={{ background: sev.bg, color: sev.color, border: `1px solid ${sev.color}55` }}
                          >
                            {it.business_days_pending}d
                          </span>
                        </td>
                        <td className="px-3 py-2.5 font-mono text-xs text-slate-300">{it.expense_date}</td>
                        <td className="px-3 py-2.5 text-slate-200">{it.vendor || <span className="text-slate-500 italic">Sin proveedor</span>}</td>
                        <td className="px-3 py-2.5">
                          {it.project_code && (
                            <>
                              <div className="text-[10px]" style={{ color: "var(--cyan)" }}>{it.project_code}</div>
                              <div className="text-xs text-slate-300 truncate max-w-[200px]">{it.project_name}</div>
                            </>
                          )}
                        </td>
                        <td className="px-3 py-2.5 text-xs text-slate-300 max-w-[260px]">
                          <div className="truncate" title={it.description}>{it.description}</div>
                          <div className="text-[10px] text-slate-500 mt-0.5">{it.category}</div>
                        </td>
                        <td className="px-3 py-2.5 text-right font-mono font-bold text-slate-100 tabular-nums">{fmtMXN(it.amount)}</td>
                        <td className="px-3 py-2.5">
                          <div className="flex items-center justify-center gap-1">
                            <button
                              data-testid={`mark-invoiced-${it.id}`}
                              onClick={() => setMarkModal(it)}
                              className="btn-primary px-2.5 py-1 rounded-sm text-[10px] uppercase tracking-widest"
                              title="Capturar UUID/folio del CFDI recibido"
                            >
                              Facturado
                            </button>
                            <button
                              data-testid={`no-invoice-${it.id}`}
                              onClick={() => setNoInvoiceModal(it)}
                              className="btn-ghost px-2.5 py-1 rounded-sm text-[10px] uppercase tracking-widest"
                              title="Marcar como consumo final (no requiere CFDI)"
                              style={{ color: "#94a3b8" }}
                            >
                              Sin factura
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {markModal && (
        <MarkInvoicedModal item={markModal} onClose={() => setMarkModal(null)} onDone={() => { setMarkModal(null); load(); }} />
      )}
      {noInvoiceModal && (
        <NoInvoiceModal item={noInvoiceModal} onClose={() => setNoInvoiceModal(null)} onDone={() => { setNoInvoiceModal(null); load(); }} />
      )}
    </div>
  );
}

function Kpi({ label, value, icon: Icon, color }) {
  return (
    <div className="panel p-3">
      <div className="flex items-center justify-between">
        <div className="label-mono text-[10px]">{label}</div>
        <Icon className="w-3.5 h-3.5" style={{ color }} />
      </div>
      <div className="font-mono font-bold text-lg tabular-nums mt-1" style={{ color }}>{value}</div>
    </div>
  );
}

function MarkInvoicedModal({ item, onClose, onDone }) {
  const [form, setForm] = useState({
    invoice_uuid: "",
    invoice_folio: "",
    invoice_received_at: new Date().toISOString().slice(0, 10),
    invoice_notes: "",
  });
  const [saving, setSaving] = useState(false);
  const [showScanner, setShowScanner] = useState(false);
  const [scanned, setScanned] = useState(null);

  const totalMismatch = scanned?.total != null && Math.abs(scanned.total - item.amount) > 0.01;

  const onScanResult = (result) => {
    setShowScanner(false);
    setScanned(result);
    setForm((f) => ({ ...f, invoice_uuid: result.uuid }));
    toast.success("QR leído: UUID capturado automáticamente");
  };

  const save = async (e) => {
    e.preventDefault();
    if (saving) return;
    setSaving(true);
    try {
      await api.patch(`/expenses/${item.id}/mark-invoiced`, form);
      toast.success("Gasto marcado como facturado");
      onDone();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al marcar como facturado");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: "rgba(5,8,18,0.85)", backdropFilter: "blur(6px)" }} onClick={onClose}>
      <form onClick={(e) => e.stopPropagation()} onSubmit={save} className="w-full max-w-md panel-strong" data-testid="mark-invoiced-modal">
        <div className="px-6 py-4 border-b flex items-center justify-between" style={{ borderColor: "var(--border)" }}>
          <div>
            <div className="label-mono">// MARCAR COMO FACTURADO</div>
            <h2 className="font-display font-bold text-lg text-slate-100 mt-1">{item.vendor || "Proveedor"} · {fmtMXN(item.amount)}</h2>
          </div>
          <button type="button" onClick={onClose} className="btn-ghost p-1.5 rounded-sm"><X className="w-4 h-4" /></button>
        </div>
        <div className="px-6 py-5 space-y-3">
          <button
            type="button"
            data-testid="scan-qr-btn"
            onClick={() => setShowScanner(true)}
            className="w-full px-4 py-2.5 rounded-sm text-xs uppercase tracking-widest font-semibold flex items-center justify-center gap-2"
            style={{ background: "rgba(34,211,238,0.12)", color: "var(--cyan)", border: "1px dashed var(--cyan)" }}
          >
            <QrCode className="w-4 h-4" /> Escanear QR de la factura
          </button>

          {scanned && (
            <div
              className="px-3 py-2.5 rounded-sm text-xs space-y-1"
              data-testid="scan-result-banner"
              style={{
                background: totalMismatch ? "rgba(251,191,36,0.1)" : "rgba(34,197,94,0.1)",
                border: `1px solid ${totalMismatch ? "rgba(251,191,36,0.4)" : "rgba(34,197,94,0.4)"}`,
              }}
            >
              <div className="flex items-center gap-1.5 font-semibold" style={{ color: totalMismatch ? "#fbbf24" : "#22c55e" }}>
                {totalMismatch ? <AlertTriangle className="w-3.5 h-3.5" /> : <CheckCircle2 className="w-3.5 h-3.5" />}
                {totalMismatch ? "El total del CFDI no coincide con el gasto" : "QR verificado"}
              </div>
              {scanned.total != null && (
                <div className="text-slate-300 font-mono">
                  Total CFDI: <b>{fmtMXN(scanned.total)}</b> · Gasto: <b>{fmtMXN(item.amount)}</b>
                </div>
              )}
              {scanned.rfc_emisor && (
                <div className="text-slate-400">RFC emisor: <span className="font-mono">{scanned.rfc_emisor}</span></div>
              )}
              {totalMismatch && (
                <div className="text-[10px] text-slate-400">Puedes confirmar de todas formas si la diferencia es correcta.</div>
              )}
            </div>
          )}

          <div>
            <label className="label-mono block mb-1">UUID CFDI *</label>
            <input
              required
              data-testid="invoice-uuid"
              placeholder="A1B2C3D4-E5F6-7890-ABCD-EF1234567890"
              value={form.invoice_uuid}
              onChange={(e) => setForm({ ...form, invoice_uuid: e.target.value.toUpperCase() })}
              className="input-cyber px-3 py-2 text-sm w-full font-mono"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="label-mono block mb-1">Folio interno</label>
              <input
                data-testid="invoice-folio"
                placeholder="F-2026-042"
                value={form.invoice_folio}
                onChange={(e) => setForm({ ...form, invoice_folio: e.target.value })}
                className="input-cyber px-3 py-2 text-sm w-full"
              />
            </div>
            <div>
              <label className="label-mono block mb-1">Fecha recibida *</label>
              <input
                required
                type="date"
                data-testid="invoice-received-at"
                value={form.invoice_received_at}
                onChange={(e) => setForm({ ...form, invoice_received_at: e.target.value })}
                className="input-cyber px-3 py-2 text-sm w-full"
              />
            </div>
          </div>
          <div>
            <label className="label-mono block mb-1">Notas (opcional)</label>
            <textarea
              data-testid="invoice-notes"
              rows={2}
              placeholder="Observaciones, complemento de pago, etc."
              value={form.invoice_notes}
              onChange={(e) => setForm({ ...form, invoice_notes: e.target.value })}
              className="input-cyber px-3 py-2 text-sm w-full resize-none"
            />
          </div>
        </div>
        <div className="px-6 py-4 border-t flex justify-end gap-2" style={{ borderColor: "var(--border)" }}>
          <button type="button" onClick={onClose} className="btn-ghost px-4 py-2 rounded-sm text-xs uppercase tracking-widest">Cancelar</button>
          <button type="submit" disabled={saving} data-testid="mark-invoiced-submit" className="btn-primary px-4 py-2 rounded-sm text-xs uppercase tracking-widest flex items-center gap-2">
            {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5" />}
            Confirmar
          </button>
        </div>
        {showScanner && <QrCfdiScanner onResult={onScanResult} onClose={() => setShowScanner(false)} />}
      </form>
    </div>
  );
}

function NoInvoiceModal({ item, onClose, onDone }) {
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);

  const save = async (e) => {
    e.preventDefault();
    if (saving) return;
    setSaving(true);
    try {
      await api.patch(`/expenses/${item.id}/mark-no-invoice`, { invoice_notes: notes });
      toast.success("Gasto marcado como consumo final");
      onDone();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al marcar");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: "rgba(5,8,18,0.85)", backdropFilter: "blur(6px)" }} onClick={onClose}>
      <form onClick={(e) => e.stopPropagation()} onSubmit={save} className="w-full max-w-md panel-strong" data-testid="no-invoice-modal">
        <div className="px-6 py-4 border-b" style={{ borderColor: "var(--border)" }}>
          <div className="label-mono">// SIN FACTURA (CONSUMO FINAL)</div>
          <h2 className="font-display font-bold text-base text-slate-100 mt-1">¿Confirmar que este gasto no requiere CFDI?</h2>
          <p className="text-xs text-slate-400 mt-2">{item.vendor || "Proveedor"} · {fmtMXN(item.amount)} · {item.description}</p>
        </div>
        <div className="px-6 py-5">
          <label className="label-mono block mb-1">Razón (opcional)</label>
          <textarea
            data-testid="no-invoice-notes"
            rows={2}
            placeholder="Ej. compra menor en tienda local sin RFC"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            className="input-cyber px-3 py-2 text-sm w-full resize-none"
          />
          <p className="text-[11px] text-slate-500 mt-2">
            Este gasto saldrá del listado de pendientes pero quedará registrado como <b>no deducible</b>.
          </p>
        </div>
        <div className="px-6 py-4 border-t flex justify-end gap-2" style={{ borderColor: "var(--border)" }}>
          <button type="button" onClick={onClose} className="btn-ghost px-4 py-2 rounded-sm text-xs uppercase tracking-widest">Cancelar</button>
          <button type="submit" disabled={saving} data-testid="no-invoice-submit" className="px-4 py-2 rounded-sm text-xs uppercase tracking-widest font-semibold" style={{ background: "rgba(239,68,68,0.15)", color: "#fca5a5", border: "1px solid rgba(239,68,68,0.4)" }}>
            {saving ? "..." : "Confirmar sin factura"}
          </button>
        </div>
      </form>
    </div>
  );
}
